import { forwardRef, type ComponentPropsWithoutRef, type ElementRef } from "react"
import * as TooltipPrimitive from "@radix-ui/react-tooltip"

import { cn } from "../../lib/cn"

const TooltipProvider = TooltipPrimitive.Provider

const Tooltip = TooltipPrimitive.Root

const TooltipTrigger = TooltipPrimitive.Trigger

// Content is rendered inside a Portal (canonical shadcn/Radix pattern). Without
// the Portal the popover mounts inline in the trigger's subtree, so any ancestor
// that establishes a clipping/stacking context — the dashboard's `.glass-widget`
// cards use `backdrop-filter: blur()`, which does exactly that — clips the popover
// or paints a sibling card over it. That is why the per-widget ⓘ tooltips showed
// only their aria-label with no visible popover, and why the header health pill's
// tooltip rendered behind the "Dashboard" H1. Portaling to <body> escapes every
// ancestor context so the `z-50` content wins reliably.
const TooltipContent = forwardRef<
    ElementRef<typeof TooltipPrimitive.Content>,
    ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 4, ...props }, ref) => (
    <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
            ref={ref}
            sideOffset={sideOffset}
            className={cn(
                "z-50 overflow-hidden rounded-md border bg-popover px-3 py-1.5 text-sm text-popover-foreground shadow-md animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
                className
            )}
            {...props}
        />
    </TooltipPrimitive.Portal>
))
TooltipContent.displayName = TooltipPrimitive.Content.displayName

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider }
